import React, { FC, useCallback, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import './Login.css';

import AuthApi from '../../../API/auth/authApi';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';

import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import USERS from '../../../common/data/userDummyData';
import { axiosWi } from '../../../API/axiosInstance';
import { loginToReCity } from '../../../API/auth/recityAuthApi';

const Login: FC = () => {
	const { setUser, setToken } = useContext(AuthContext);
	const { darkModeStatus } = useDarkMode();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(false);

	const formik = useFormik({
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: any = {};
			if (!values.loginUsername) errors.loginUsername = 'Required';
			if (!values.loginPassword) errors.loginPassword = 'Required';
			return errors;
		},
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				const res = await AuthApi.loginApi({
					username: values.loginUsername,
					password: values.loginPassword,
				});

				if (res.data?.token) {
					setUser(JSON.stringify(res.data));
					setToken(res.data.token);

					try {
						await loginToReCity(values.loginUsername, values.loginPassword);
					} catch (e) {
						console.warn('Recity login failed');
					}

					getCities(res.data.roleId, res.data.city);
				}
			} catch (err: any) {
				formik.setFieldError(
					'loginUsername',
					err?.response?.data?.message || 'Login failed',
				);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const getCities = async (roleId: number, userCity: string) => {
		const res = await axiosWi.get('city/total-cities');
		localStorage.setItem('allCities', JSON.stringify(res.data.data));
		localStorage.setItem('loggedInCity', userCity);
		navigate('/dashboard');
	};

	return (
		<PageWrapper isProtected={false} title='Login' className='p-0'>
			<div className='d-flex min-vh-100 w-100 align-items-center justify-content-center bg-login'>
				<div className='login-container'>
					<Card className='login-card'>
						<CardBody className='p-4'>
							<div className='text-center mb-4'>
								<Logo width={180} />
								<h2 className='fw-bold mt-3'>Welcome Back</h2>
							</div>

							<Alert isLight icon='Lock'>
								<strong>Demo:</strong> {USERS.JOHN.username} / {USERS.JOHN.password}
							</Alert>

							<form onSubmit={formik.handleSubmit} className='row g-4'>
								<div className='col-12'>
									<FormGroup id='loginUsername' label='Username or Email'>
										<Input
											value={formik.values.loginUsername}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											isTouched={formik.touched.loginUsername}
											invalidFeedback={formik.errors.loginUsername}
										/>
									</FormGroup>
								</div>

								<div className='col-12'>
									<FormGroup id='loginPassword' label='Password'>
										<Input
											type='password'
											value={formik.values.loginPassword}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											isTouched={formik.touched.loginPassword}
											invalidFeedback={formik.errors.loginPassword}
										/>
									</FormGroup>
								</div>

								<div className='col-12'>
									<Button
										type='submit'
										color='warning'
										className='w-100 py-3'
										isDisable={isLoading}>
										{isLoading ? <Spinner size='sm' /> : 'Login'}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</div>
			</div>
		</PageWrapper>
	);
};

export default Login;
